import { useMemo } from "react";
import { Link } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { agoraSP, formatTimestampSP } from "@/lib/date";
import { useQuadro } from "@/hooks/useQuadro";
import { ETAPA_COR, etapasEmAndamento } from "@shared/etapas";

// ── SolarEdge palette ────────────────────────────────────────────────────────
const INK    = "#1A1917";
const YELLOW = "#3F3F46";
const AMBER  = "#52525B";
const ORANGE = "#71717A";
const MUTE   = "#737373";


const REDE_COLORS: Record<string, string> = {
  instagram: ORANGE, tiktok: INK, youtube: "#DC2626",
  linkedin: "#0A66C2", twitter: "#0EA5E9",
};

// ── Helpers ──────────────────────────────────────────────────────────────────
function monthLabel(mes: string) {
  try {
    return new Date(mes + "-01T12:00:00").toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
  } catch { return mes; }
}
function greeting() {
  const h = agoraSP().hora;
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

// ── Score Ring (estilo "86" do shot) ─────────────────────────────────────────
function ScoreRing({ value, label = "score" }: { value: number; label?: string }) {
  const v = Math.max(0, Math.min(100, value));
  const R = 28, C = 2 * Math.PI * R;
  const dash = (v / 100) * C;
  return (
    <div className="relative w-[72px] h-[72px] shrink-0">
      <svg viewBox="0 0 72 72" className="w-full h-full -rotate-90">
        <circle cx="36" cy="36" r={R} stroke="#E7E3DA" strokeWidth="5" fill="none" />
        <circle
          cx="36" cy="36" r={R}
          stroke="#4F46E5" strokeWidth="5" fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${C - dash}`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="num-hero text-[20px]">{v}</span>
        <span className="text-[9px] text-[var(--solar-mute)] uppercase tracking-widest -mt-0.5">{label}</span>
      </div>
    </div>
  );
}

// ── KPI card — variantes light / dark ────────────────────────────────────────
function KpiCard({
  label, value, sub, dark, accent, href,
}: {
  label: string;
  value: number | string;
  sub?: string;
  dark?: boolean;
  accent?: string;
  href?: string;
}) {
  const card = (
    <div className={`rounded-[14px] p-5 flex flex-col gap-3 transition-all ${dark
      ? "bg-[var(--solar-dark)] text-white hover:opacity-90"
      : "bg-[var(--solar-card)] border border-[var(--solar-line)] hover:border-[#D6D3CD]"
      } ${href ? "cursor-pointer" : ""}`}>
      <p className={`text-[11px] font-medium uppercase tracking-[0.12em] ${dark ? "text-white/55" : "text-[var(--solar-mute)]"}`}>
        {label}
      </p>
      <div className="flex items-end justify-between">
        <span className="num-hero text-[40px]" style={{ color: dark ? "#fff" : INK }}>
          {value}
        </span>
        {accent && <span className="w-2 h-2 rounded-full mb-2.5" style={{ background: accent }} />}
      </div>
      {sub && <p className={`text-[11px] ${dark ? "text-white/55" : "text-[var(--solar-mute)]"}`}>{sub}</p>}
    </div>
  );
  return href ? <Link href={href}>{card}</Link> : card;
}

// ── Pipeline bar (estilo SolarEdge: pílulas) ─────────────────────────────────
function PipelineBar({ counts }: { counts: Record<string, number> }) {
  // Etapas do quadro do cliente, na ordem dele (out/26: era lista fixa com
  // "Roteiro" e sem Em Captação/Em Produção de Criação). Card em etapa que o
  // quadro não usa entra numa linha "Fora do quadro".
  const { etapasPauta, noQuadro } = useQuadro();
  const fora = Object.entries(counts).filter(([id]) => !noQuadro(id)).reduce((s, [, v]) => s + v, 0);
  const KANBAN_COLS = [
    ...etapasPauta.map(c => ({ id: c.id, label: c.label, color: ETAPA_COR[c.id] ?? MUTE })),
    ...(fora > 0 ? [{ id: "__fora", label: "Fora do quadro", color: "#B42318" }] : []),
  ];
  const valor = (id: string) => (id === "__fora" ? fora : counts[id] ?? 0);
  const total = Object.values(counts).reduce((s, v) => s + v, 0) || 1;
  return (
    <div className="se-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[15px] font-bold text-[var(--solar-ink)]">Pipeline de Pautas</h3>
        <span className="se-label">{total} no total</span>
      </div>
      <div className="flex h-2.5 rounded-full overflow-hidden gap-1 mb-5 bg-[#EDEBE6]">
        {KANBAN_COLS.map(col => {
          const cnt = valor(col.id);
          if (cnt === 0) return null;
          return (
            <div
              key={col.id}
              style={{ background: col.color, width: `${(cnt / total) * 100}%`, minWidth: 3 }}
              title={`${col.label}: ${cnt}`}
            />
          );
        })}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2.5">
        {KANBAN_COLS.map(col => {
          const cnt = valor(col.id);
          return (
            <div key={col.id} className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: col.color }} />
              <span className="text-[12px] text-[var(--solar-mute)] flex-1">{col.label}</span>
              <span className="text-[12px] font-bold text-[var(--solar-ink)]">{cnt}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Rede chart (barras horizontais com gradiente amarelo) ────────────────────
function RedeChart({ porRede }: { porRede: Record<string, number> }) {
  const entries = Object.entries(porRede).sort(([, a], [, b]) => b - a);
  const max = entries[0]?.[1] ?? 1;
  return (
    <div className="se-card">
      <h3 className="text-[15px] font-bold text-[var(--solar-ink)] mb-4">Pautas por Rede</h3>
      <div className="space-y-3">
        {entries.map(([rede, cnt]) => (
          <div key={rede}>
            <div className="flex justify-between mb-1.5">
              <span className="text-[12px] font-medium text-[var(--solar-ink)] capitalize">{rede}</span>
              <span className="text-[12px] font-bold text-[var(--solar-ink)]">{cnt}</span>
            </div>
            <div className="h-2 bg-[#EDEBE6] rounded-full overflow-hidden">
              <div
                className="h-2 rounded-full"
                style={{
                  background: `linear-gradient(90deg, ${REDE_COLORS[rede] ?? AMBER}, ${YELLOW})`,
                  width: `${(cnt / max) * 100}%`,
                }}
              />
            </div>
          </div>
        ))}
        {entries.length === 0 && <p className="text-xs text-[var(--solar-mute)] text-center py-4">Sem dados</p>}
      </div>
    </div>
  );
}

// ── Trend chart (estilo SolarEdge: linha sutil + área amarela) ───────────────
function TrendChart({ meses }: { meses: { mes: string; pautas: number; textos: number }[] }) {
  if (meses.length === 0) return null;
  const maxVal = Math.max(...meses.flatMap(m => [m.pautas, m.textos]), 1);
  const W = 100, H = 60;
  const n = meses.length;
  function pts(key: "pautas" | "textos") {
    return meses.map((m, i) => {
      const x = n === 1 ? W / 2 : (i / (n - 1)) * W;
      const y = H - (m[key] / maxVal) * H;
      return `${x},${y}`;
    }).join(" ");
  }
  const areaPts = `0,${H} ${pts("pautas")} ${W},${H}`;
  return (
    <div className="se-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-[15px] font-bold text-[var(--solar-ink)]">Produção · últimos 6 meses</h3>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: AMBER }} /><span className="text-[11px] text-[var(--solar-mute)]">Pautas</span></span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: INK }} /><span className="text-[11px] text-[var(--solar-mute)]">Textos</span></span>
        </div>
      </div>
      <div className="relative mt-4">
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-24">
          <defs>
            <linearGradient id="trend-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={YELLOW} stopOpacity="0.35" />
              <stop offset="100%" stopColor={YELLOW} stopOpacity="0" />
            </linearGradient>
          </defs>
          <polygon points={areaPts} fill="url(#trend-area)" />
          <polyline points={pts("pautas")} fill="none" stroke={AMBER} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <polyline points={pts("textos")} fill="none" stroke={INK} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="2 1.5" />
        </svg>
        <div className="flex justify-between mt-2">
          {meses.map(m => (
            <span key={m.mes} className="text-[10px] text-[var(--solar-mute)] capitalize">{monthLabel(m.mes)}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Quick link card ──────────────────────────────────────────────────────────
function QuickLink({ href, label, sub, icon }: { href: string; label: string; sub: string; icon: React.ReactNode }) {
  return (
    <Link href={href}>
      <div className="se-card !p-4 flex items-center gap-3 hover:border-[#D6D3CD] transition-all cursor-pointer">
        <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-[var(--solar-ink)] text-white">
          {icon}
        </div>
        <div className="flex-1">
          <p className="text-[13px] font-bold text-[var(--solar-ink)]">{label}</p>
          <p className="text-[11px] text-[var(--solar-mute)]">{sub}</p>
        </div>
        <svg className="w-3.5 h-3.5 text-[var(--solar-mute)] ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </Link>
  );
}

// ── Página ───────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { session } = useAuth();
  const clientId = session?.activeClientId ?? session?.clientId ?? 1;

  const { data: summary } = trpc.metricas.summary.useQuery({ clientId }, { enabled: !!clientId });
  const { data: pautaCounts = {} } = trpc.pautas.counts.useQuery({ clientId }, { enabled: !!clientId });
  const { data: temas = [] } = trpc.temas.list.useQuery({ clientId }, { enabled: !!clientId });

  const temasEmSugestao = useMemo(() => temas.filter(t => t.status === "sugestao").length, [temas]);
  // Em andamento = etapas entre a inicial e o fim, no quadro do cliente.
  const { quadro } = useQuadro(clientId);
  const pautasEmProducao = useMemo(() => {
    return etapasEmAndamento(quadro).reduce((s, c) => s + ((pautaCounts as any)[c.id] ?? 0), 0);
  }, [pautaCounts, quadro]);

  // "Score" derivado: ratio aprovadas/total
  const totalPautas = summary?.pautas.total ?? 0;
  const aprovadas = pautaCounts["aprovado"] ?? 0;
  const publicadas = pautaCounts["publicado"] ?? 0;
  const score = totalPautas > 0
    ? Math.round(((aprovadas + publicadas) / totalPautas) * 100)
    : 0;

  const datePtBR = formatTimestampSP(Date.now(), {
    weekday: "long", day: "numeric", month: "long", year: "numeric"
  });

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-5">

        {/* Header */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[12px] text-[var(--solar-mute)] capitalize">{datePtBR}</p>
            <h1 className="text-[28px] mt-1 font-extrabold tracking-tight" style={{ fontFamily: "Poppins, sans-serif" }}>
              {greeting()}, {session?.name?.split(" ")[0] ?? "time"}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/insights"><button className="se-pill">Insights</button></Link>
            <Link href="/pautas">
              <button className="se-btn">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Nova pauta
              </button>
            </Link>
          </div>
        </div>

        {/* Hero KPI block: 4 cards + score ring (estilo "Current Power") */}
        <div className="se-card !p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-[13px] font-semibold text-[var(--solar-ink)]">Visão geral</p>
              <p className="text-[11px] text-[var(--solar-mute)] mt-0.5">Atualizado agora</p>
            </div>
            <ScoreRing value={score} label="aprov." />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              label="Pautas totais"
              value={summary?.pautas.total ?? 0}
              sub={`${pautasEmProducao} em andamento`}
              accent={AMBER}
              href="/pautas"
            />
            <KpiCard
              label="Aprovação"
              value={(pautaCounts["revisao"] ?? 0) + (pautaCounts["revisao_design"] ?? 0)}
              sub="Redação + Design"
              accent={ORANGE}
              href="/pautas"
            />
            <KpiCard
              label="Em produção"
              value={pautasEmProducao}
              sub={(() => { const e = etapasEmAndamento(quadro); return e.length ? `de ${e[0].label} a ${e[e.length - 1].label}` : ""; })()}
              dark
              href="/pautas"
            />
            <KpiCard
              label="Temas"
              value={temasEmSugestao}
              sub="sugestões pendentes"
              accent={YELLOW}
              href="/temas"
            />
          </div>
        </div>

        {/* Pipeline + Rede */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <PipelineBar counts={pautaCounts} />
          </div>
          <RedeChart porRede={summary?.pautas.porRede ?? {}} />
        </div>

        {/* Trend + Quick links */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <TrendChart meses={summary?.ultimosMeses ?? []} />
          </div>
          <div className="flex flex-col gap-2.5">
            <h3 className="text-[10px] font-bold text-[var(--solar-mute)] uppercase tracking-[0.18em] px-1">
              Atalhos
            </h3>
            <QuickLink href="/temas" label="Temas" sub="Calendário editorial"
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>} />
            <QuickLink href="/calendario" label="Calendário" sub="Visão mensal"
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>} />
            <QuickLink href="/utms" label="UTMs" sub="Rastreamento de links"
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>} />
            <QuickLink href="/relatorios" label="Relatórios" sub="Exportar resumo mensal"
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>} />
          </div>
        </div>

        {/* Stats footer */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(["instagram","tiktok","youtube","linkedin"] as const).map(rede => {
            const cnt = summary?.pautas.porRede?.[rede] ?? 0;
            return (
              <div key={rede} className="se-card !p-4 flex items-center gap-3">
                <div className="w-2 h-2 rounded-full shrink-0" style={{ background: REDE_COLORS[rede] }} />
                <div>
                  <p className="text-[11px] text-[var(--solar-mute)] capitalize uppercase tracking-wider">{rede}</p>
                  <p className="num-hero text-[20px] mt-0.5">{cnt}</p>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}
